import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Group, Menu, Modal, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ImportDocument } from '@pst/shared';
import { ApiError, api, costsApi, projectApi, snapshotApi, templateApi, timeApi, type Project } from '../api';
import { formatDate } from '../format';
import { ImportModal } from './ImportModal';
import { ExportModal } from './ExportModal';
import { exportProject, scheduleStart, shiftByWeeks, toDuplicate, toTemplate, weeksBetween } from './transfer';
import { useCan } from './useMe';
import { downloadLink, linkTo } from '../ui/links';
import { SubMenu } from '../ui/SubMenu';
import { CORE_VIEWS, MODULE_VIEWS } from './moduleViews';

type Dialog =
  | { kind: 'duplicate'; project: Project }
  | { kind: 'template'; project: Project }
  | { kind: 'delete'; project: Project }
  | { kind: 'replace'; project: Project }
  | { kind: 'export'; project: Project }
  | { kind: 'fromTemplate' }
  | { kind: 'import'; doc: ImportDocument; target?: Project };

/**
 * What can be done with a project as a whole (rebuilt in AP-101): open it,
 * in a new tab or straight in an area; team and snapshots; everything to
 * download in one submenu; copy and take over; delete, set apart at the end.
 * Shared by the right-click menu and the ⋯ button on each project tile.
 */
export function useProjectActions(): {
  items: (project: Project, close: () => void) => JSX.Element;
  startFromTemplate: () => void;
  dialogs: JSX.Element;
} {
  const navigate = useNavigate();
  const canWrite = useCan('projects.write');
  const canDelete = useCan('projects.delete');
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const active = new Set((modules.data ?? []).filter((m) => m.active).map((m) => m.key));
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const close = (): void => setDialog(null);

  const items = (project: Project, done: () => void): JSX.Element => {
    const run = (action: () => void) => () => {
      done();
      action();
    };
    const home = `/projekte/${project.id}`;
    const schedule = `/api/scheduling/projects/${project.id}/export`;
    const areas = [
      ...MODULE_VIEWS.filter((v) => active.has(v.module)),
      ...CORE_VIEWS,
    ];
    const snapshot = (): void => {
      snapshotApi.take(project.id, 'manual')
        .then((r) => setNotice(r.created ? `Snapshot von ${project.number} angelegt.` : `${project.number}: seit dem letzten Snapshot nichts geändert – kein neuer angelegt.`))
        .catch((e: unknown) => setNotice(e instanceof ApiError ? e.message : 'Der Snapshot ist fehlgeschlagen.'));
    };
    return (
      <>
        <Menu.Label>{project.number} · {project.name.length > 34 ? `${project.name.slice(0, 34)} …` : project.name}</Menu.Label>
        <Menu.Item component="a" {...linkTo(navigate, home, done)}>Öffnen</Menu.Item>
        <Menu.Item component="a" href={home} target="_blank" rel="noopener" onClick={done}>In neuem Tab öffnen</Menu.Item>
        <SubMenu label="Bereich öffnen">
            {areas.map((v) => (
              <Menu.Item key={v.path} component="a" {...linkTo(navigate, `${home}/${v.path}`, done)}>{v.label}</Menu.Item>
            ))}
          </SubMenu>

        <Menu.Divider />
        <Menu.Label>Projekt</Menu.Label>
        <Menu.Item component="a" {...linkTo(navigate, `${home}/team`, done)}>Team …</Menu.Item>
        {canWrite && <Menu.Item onClick={run(snapshot)}>Snapshot jetzt anlegen</Menu.Item>}
        <Menu.Item component="a" {...linkTo(navigate, `${home}/snapshots`, done)}>Snapshots …</Menu.Item>

        <Menu.Divider />
        <SubMenu label="Herunterladen">
            <Menu.Item onClick={run(() => setDialog({ kind: 'export', project }))}>PST-Datei (JSON) …</Menu.Item>
            {active.has('scheduling') && (
              <>
                <Menu.Divider />
                <Menu.Label>Terminplan</Menu.Label>
                <Menu.Item component="a" {...linkTo(navigate, `${home}/termine/druck?format=A4`, done)}>Druckansicht A4 quer</Menu.Item>
                <Menu.Item component="a" {...linkTo(navigate, `${home}/termine/druck?format=A3`, done)}>Druckansicht A3 quer</Menu.Item>
                <Menu.Item component="a" {...downloadLink(`${schedule}/mspdi`, done)}>MSPDI-XML</Menu.Item>
                <Menu.Item component="a" {...downloadLink(`${schedule}/csv`, done)}>CSV</Menu.Item>
              </>
            )}
            {active.has('costs') && (
              <>
                <Menu.Divider />
                <Menu.Label>Kosten</Menu.Label>
                <Menu.Item component="a" {...downloadLink(costsApi.csvUrl(project.id), done)}>Kostenplan als CSV</Menu.Item>
              </>
            )}
            {active.has('projectdata') && (
              <>
                <Menu.Divider />
                <Menu.Label>Zeiten</Menu.Label>
                <Menu.Item component="a" {...downloadLink(timeApi.csvUrl(project.id), done)}>Buchungen als CSV</Menu.Item>
              </>
            )}
          </SubMenu>

        {canWrite && (
          <>
            <Menu.Divider />
            <Menu.Label>Kopieren und Übernehmen</Menu.Label>
            <Menu.Item onClick={run(() => setDialog({ kind: 'duplicate', project }))}>Duplizieren …</Menu.Item>
            <Menu.Item onClick={run(() => setDialog({ kind: 'template', project }))}>Als Vorlage speichern …</Menu.Item>
            <Menu.Item onClick={run(() => setDialog({ kind: 'replace', project }))}>PST-Datei in dieses Projekt übernehmen …</Menu.Item>
          </>
        )}
        {canDelete && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={run(() => setDialog({ kind: 'delete', project }))}>Löschen …</Menu.Item>
          </>
        )}
      </>
    );
  };

  const dialogs = (
    <>
      {notice && (
        <Modal opened onClose={() => setNotice(null)} title="Hinweis" size="sm">
          <Text size="sm">{notice}</Text>
        </Modal>
      )}
      {dialog?.kind === 'duplicate' && (
        <DuplicateModal
          project={dialog.project}
          active={active}
          onClose={close}
          onReady={(doc) => setDialog({ kind: 'import', doc })}
        />
      )}
      {dialog?.kind === 'template' && <SaveTemplateModal project={dialog.project} active={active} onClose={close} />}
      {dialog?.kind === 'delete' && <DeleteModal project={dialog.project} onClose={close} />}
      {dialog?.kind === 'export' && <ExportModal project={dialog.project} active={active} onClose={close} onError={setNotice} />}
      {dialog?.kind === 'fromTemplate' && <FromTemplateModal onClose={close} onReady={(doc) => setDialog({ kind: 'import', doc })} />}
      <ImportModal opened={dialog?.kind === 'replace'} onClose={close} target={dialog?.kind === 'replace' ? dialog.project : undefined} />
      <ImportModal opened={dialog?.kind === 'import'} onClose={close} initial={dialog?.kind === 'import' ? dialog.doc : undefined} />
    </>
  );

  return { items, startFromTemplate: () => setDialog({ kind: 'fromTemplate' }), dialogs };
}

function DuplicateModal({
  project,
  active,
  onClose,
  onReady,
}: {
  project: Project;
  active: ReadonlySet<string>;
  onClose: () => void;
  onReady: (doc: ImportDocument) => void;
}): JSX.Element {
  const [number, setNumber] = useState(`${project.number}-2`);
  const [name, setName] = useState(`${project.name} (Kopie)`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal opened onClose={onClose} title={`${project.number} duplizieren`}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          exportProject(project, active)
            .then((doc) => onReady(toDuplicate(doc, number.trim(), name.trim())))
            .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Duplizieren fehlgeschlagen.'))
            .finally(() => setBusy(false));
        }}
      >
        <Stack gap="sm">
          {error && <Alert color="red">{error}</Alert>}
          <Text size="sm">Kopiert werden Projektdaten, Kontakte, Terminplan, Kostenplanung, Vergabeeinheiten mit LV und die Kategorien der Zeiterfassung. Nicht kopiert: Team, Basispläne, Bietende, Preise, Zuschläge und gebuchte Zeiten.</Text>
          <TextInput label="Neue Projektnummer" required value={number} onChange={(e) => setNumber(e.currentTarget.value)} data-autofocus />
          <TextInput label="Bezeichnung" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" loading={busy} disabled={!number.trim() || !name.trim()}>Weiter zur Prüfung</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function SaveTemplateModal({ project, active, onClose }: { project: Project; active: ReadonlySet<string>; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState('');
  const save = useMutation({
    mutationFn: async () => {
      const doc = toTemplate(await exportProject(project, active));
      return templateApi.create({ name: name.trim(), description: description.trim() || null, document: doc });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
  });
  return (
    <Modal opened onClose={onClose} title="Als Vorlage speichern">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <Stack gap="sm">
          {save.isError && <Alert color="red">{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
          <Text size="sm">
            Die Vorlage enthält Phasen, Vorgänge, Meilensteine, Abhängigkeiten, Annahmen, Risiken und das Wochenraster –
            dazu die Kategorien der Zeiterfassung ohne Budget – aber keine Kontakte, keine Auftraggebenden, keine Angebotszahlen, keine Kosten und keine Vergabe. Alle Vorgänge stehen wieder auf „offen“.
          </Text>
          <TextInput label="Name der Vorlage" required value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          <Textarea label="Beschreibung" autosize minRows={2} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim()}>Speichern</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function DeleteModal({ project, onClose }: { project: Project; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const remove = useMutation({
    mutationFn: () => projectApi.remove(project.id, confirm.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
      navigate('/');
    },
  });
  return (
    <Modal opened onClose={onClose} title={`${project.number} löschen`}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (confirm.trim() === project.number) remove.mutate();
        }}
      >
        <Stack gap="sm">
          {remove.isError && <Alert color="red">{remove.error instanceof ApiError ? remove.error.message : 'Löschen fehlgeschlagen.'}</Alert>}
          <Alert color="red" variant="light">
            „{project.name}“ wird mit allem gelöscht: Terminplan, Basispläne, Projektdaten, Kontakte, Team. Das lässt sich in
            PST nicht rückgängig machen – nur über eine Sicherung (siehe docs/BACKUP.md).
          </Alert>
          <TextInput
            label={`Zur Bestätigung die Projektnummer eingeben: ${project.number}`}
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            data-autofocus
            autoComplete="off"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" color="red" loading={remove.isPending} disabled={confirm.trim() !== project.number}>Endgültig löschen</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function nextMonday(): string {
  const d = new Date();
  const add = ((8 - d.getDay()) % 7) || 7;
  d.setDate(d.getDate() + add);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function FromTemplateModal({ onClose, onReady }: { onClose: () => void; onReady: (doc: ImportDocument) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: ['templates'], queryFn: templateApi.list });
  const [id, setId] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [start, setStart] = useState(nextMonday());
  const [error, setError] = useState<string | null>(null);
  const chosen = useQuery({ queryKey: ['template', id], queryFn: () => templateApi.get(id!), enabled: id !== null });
  const doc = chosen.data?.document as ImportDocument | undefined;
  const from = doc ? scheduleStart(doc) : undefined;
  const weeks = from ? weeksBetween(from, start) : 0;
  const remove = useMutation({
    mutationFn: (templateId: string) => templateApi.remove(templateId),
    onSuccess: async () => {
      setId(null);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return (
    <Modal opened onClose={onClose} title="Neues Projekt aus Vorlage">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!doc) return;
          try {
            const shifted = shiftByWeeks(doc, weeks);
            onReady({ ...shifted, project: { ...shifted.project, number: number.trim(), name: name.trim() } });
          } catch {
            setError('Die Vorlage ist nicht lesbar.');
          }
        }}
      >
        <Stack gap="sm">
          {error && <Alert color="red">{error}</Alert>}
          {templates.isSuccess && templates.data.length === 0 ? (
            <Text size="sm" c="dimmed">
              Noch keine Vorlagen. Eine entsteht per Rechtsklick auf ein Projekt → „Als Vorlage speichern“.
            </Text>
          ) : (
            <Group align="flex-end" gap="xs" wrap="nowrap">
              <Select
                label="Vorlage"
                style={{ flex: 1 }}
                data={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
                value={id}
                onChange={(value) => {
                  setId(value);
                  const t = templates.data?.find((entry) => entry.id === value);
                  if (t && !name) setName(t.name);
                }}
                data-autofocus
              />
              {id && (
                <Button variant="subtle" color="red" onClick={() => remove.mutate(id)} loading={remove.isPending}>Vorlage löschen</Button>
              )}
            </Group>
          )}
          {chosen.data?.description && <Text size="xs" c="dimmed">{chosen.data.description}</Text>}
          <TextInput label="Projektnummer" required value={number} onChange={(e) => setNumber(e.currentTarget.value)} />
          <TextInput label="Bezeichnung" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput type="date" label="Beginn" value={start} onChange={(e) => setStart(e.currentTarget.value)} />
          {from && (
            <Text size="xs" c="dimmed">
              Die Vorlage beginnt am {formatDate(from)}; alle Termine rücken um {weeks} {Math.abs(weeks) === 1 ? 'Woche' : 'Wochen'}.
              Verschoben wird in ganzen Wochen, damit jeder Termin auf seinem Wochentag bleibt.
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={!doc || !number.trim() || !name.trim()}>Weiter zur Prüfung</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
